import React from 'react';
import {graphql, useMutation} from 'react-relay/hooks';
import classNames from 'classnames';
import {FormattedMessage} from 'react-intl';
import {getRecaptchaToken} from '../../../../utils/recaptcha';

const PollDisplay = React.memo(({poll, isAuthorized, onShowLoginPrompt}) => {
    const hasVoted = poll?.votedByCurrentUser || false;
    const votedOptionIndex = poll?.votedOptionIndex;
    const options = poll?.options || [];
    const optionVotesCounts = poll?.optionVotesCounts || [];
    const totalVotes = poll?.pollVotesCount || 0;

    const [votePoll, isVoting] = useMutation(
        graphql`
            mutation pollDisplayVotePollMutation($request: VotePollRequestInput!) {
                votePoll(request: $request) {
                    id
                    options
                    optionVotesCounts
                    pollVotesCount
                    votedByCurrentUser
                    votedOptionIndex
                }
            }
        `
    );

    const [unvotePoll, isUnvoting] = useMutation(
        graphql`
            mutation pollDisplayUnvotePollMutation($request: UnvotePollRequestInput!) {
                unvotePoll(request: $request) {
                    id
                    options
                    optionVotesCounts
                    pollVotesCount
                    votedByCurrentUser
                    votedOptionIndex
                }
            }
        `
    );

    const handleVote = React.useCallback(async (optionIndex) => {
        if (hasVoted || isVoting || isUnvoting) {
            return;
        }
        if (!isAuthorized) {
            onShowLoginPrompt?.();
            return;
        }
        try {
            const reCaptcha = await getRecaptchaToken();
            votePoll({
                variables: {
                    request: {
                        pollId: poll.id,
                        optionIndex,
                        reCaptcha
                    }
                }
            });
        } catch (error) {
            console.error('Error voting on poll:', error);
        }
    }, [poll?.id, isAuthorized, hasVoted, isVoting, isUnvoting, votePoll, onShowLoginPrompt]);

    const handleUnvote = React.useCallback(async () => {
        if (!isAuthorized || !hasVoted || isUnvoting || isVoting) {
            return;
        }
        try {
            const reCaptcha = await getRecaptchaToken();
            unvotePoll({
                variables: {
                    request: {
                        pollId: poll.id,
                        reCaptcha
                    }
                }
            });
        } catch (error) {
            console.error('Error unvoting on poll:', error);
        }
    }, [poll?.id, isAuthorized, hasVoted, isUnvoting, isVoting, unvotePoll]);

    const getOptionVotesCount = React.useCallback((optionIndex) => {
        if (optionIndex >= 0 && optionIndex < optionVotesCounts.length) {
            return optionVotesCounts[optionIndex] || 0;
        }
        return 0;
    }, [optionVotesCounts]);

    const getOptionPercentage = React.useCallback((optionIndex) => {
        if (totalVotes === 0) return 0;
        const votes = getOptionVotesCount(optionIndex);
        return Math.round((votes / totalVotes) * 100);
    }, [totalVotes, getOptionVotesCount]);

    if (!poll || !options.length) {
        return null;
    }

    return (
        <div className='margin-top-1dot5rem padding-1dot25rem'>
            <div className='display-flex flex-direction-column row-gap-0dot75rem'>
                {options.map((option, index) => {
                    const isVotedOption = hasVoted && votedOptionIndex === index;
                    const votes = getOptionVotesCount(index);
                    const percentage = hasVoted ? getOptionPercentage(index) : 0;
                    
                    return (
                        <div key={index}>
                            {!hasVoted ? (
                                <button
                                    type='button'
                                    onClick={() => handleVote(index)}
                                    disabled={isVoting || isUnvoting}
                                    className={classNames('width-100percent padding-0dot75rem border-style-solid border-width-0dot0625rem border-radius-0dot5rem text-md color-gray-900 text-align-left background-color-white cursor-pointer', {
                                        'border-color-gray-300 hover-border-color-gray-400': !isVoting && !isUnvoting,
                                        'border-color-gray-200 cursor-not-allowed opacity-0dot5': isVoting || isUnvoting
                                    })}
                                >
                                    {option}
                                </button>
                            ) : (
                                <div className='position-relative'>
                                    <div className={classNames('padding-0dot75rem border-style-solid border-width-0dot0625rem border-radius-0dot5rem text-md', {
                                        'border-color-blue-gray-500 background-color-blue-gray-50 color-gray-900': isVotedOption,
                                        'border-color-gray-200 background-color-white color-gray-700': !isVotedOption
                                    })}>
                                        <div className='display-flex align-items-center justify-content-space-between'>
                                            <span className={classNames({
                                                'semibold': isVotedOption
                                            })}>
                                                {option}
                                            </span>
                                            {totalVotes > 0 && (
                                                <span className='text-sm color-gray-500'>
                                                    <FormattedMessage
                                                        defaultMessage='{count, plural, one {# vote} other {# votes}} · {percentage}%'
                                                        values={{count: votes, percentage}}
                                                    />
                                                </span>
                                            )}
                                        </div>
                                        {totalVotes > 0 && (
                                            <div className='margin-top-0dot5rem height-0dot25rem background-color-gray-200 border-radius-0dot125rem overflow-hidden'>
                                                <div
                                                    className={classNames('height-100percent transition-all', {
                                                        'background-color-blue-gray-500': isVotedOption,
                                                        'background-color-gray-300': !isVotedOption
                                                    })}
                                                    style={{width: `${percentage}%`}}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            <div className='display-flex align-items-center justify-content-space-between padding-top-0dot75rem'>
                {totalVotes > 0 && (
                    <div className='text-sm color-gray-500'>
                        <FormattedMessage
                            defaultMessage='{count, plural, one {# vote} other {# votes}}'
                            values={{count: totalVotes}}
                        />
                    </div>
                )}
                {hasVoted && (
                    <button
                        type='button'
                        onClick={handleUnvote}
                        disabled={isUnvoting || isVoting}
                        className={classNames('text-sm color-gray-600 hover-color-gray-800 cursor-pointer background-color-transparent border-style-none padding-0', {
                            'opacity-0dot6 cursor-not-allowed': isUnvoting || isVoting
                        })}
                    >
                        <FormattedMessage defaultMessage='Remove my vote' />
                    </button>
                )}
            </div>
        </div>
    );
});

export default PollDisplay;

